// Pure offline-sync orchestration. No IndexedDB, no fetch, no DOM -- the store is
// injected (the idb adapter implements it) and the network call is injected as
// `send`. That keeps the risky logic -- draining the outbox without losing or
// duplicating an engineer's work, and overlaying pending completions on the job
// list -- fully unit-testable in node.

import type { CachedJob, OutboxItem, JobView } from "./types";
import { VISIT_DATE_TOO_OLD_ESCALATION } from "../visitDateWindow";

// ---- Outbox draining -------------------------------------------------------
export interface OutboxStore {
  getOutbox(): Promise<OutboxItem[]>;
  putOutbox(item: OutboxItem): Promise<void>;
  deleteOutbox(id: string): Promise<void>;
}

// The result of trying to sync one queued completion.
//   ok            -- the server accepted it (2xx). Remove from the outbox.
//   drop          -- it can never succeed (the job is gone -- 404/410). Remove it.
//   retry + error -- transient (offline / 5xx / lost connection). Keep and re-try.
// `structural` marks a 4xx that will not fix itself no matter how often it is re-sent (Session
// AEK / D32): three of those in a row escalate the item instead of retrying forever.
export type SendResult =
  | { ok: true }
  | { ok: false; drop: true }
  | { ok: false; drop: false; error: string; status?: number; structural?: boolean; code?: string };

export type SendFn = (item: OutboxItem) => Promise<SendResult>;

export interface FlushSummary {
  synced: number;
  dropped: number;
  failed: number;
  // Items escalated on THIS drain (they moved out of the retry loop and now need the office).
  escalated: number;
  // Everything still sitting in the outbox afterwards: the ones that will retry, plus the ones
  // that no longer will.
  remaining: number;
}

// Three consecutive structural failures is the threshold. The first two are forgiving: the office
// may fix the conflicting record between attempts.
export const ESCALATE_AFTER_STRUCTURAL_FAILURES = 3;

// ...but "three attempts" only means anything if they are SPREAD OUT. Retries here are
// event-driven, not timed: a drain fires on reconnect, on the app being foregrounded, and on the
// service worker's ping. So an engineer who finishes a job and then flicks to the camera and back
// twice can produce three attempts in about eight seconds, and nothing has had a chance to be
// fixed in between. A structural failure therefore only COUNTS if it is at least this far from
// the last counted one; failures inside the window are still recorded, they just do not advance
// the strike count. Three strikes now means at least four minutes of genuine, spaced failure.
export const MIN_STRUCTURAL_GAP_MS = 2 * 60 * 1000;

// The longest slice of a server error kept on the item. Enough for the office to diagnose,
// bounded so a huge error body never bloats IndexedDB.
export const MAX_SYNC_ERROR_CHARS = 500;

// Session BBC: a refusal that can NEVER clear by retrying or by the office fixing a record, because it is
// about time passing. visit_date_too_old (the visit is past the 2-week hard cut) only gets older, so it
// escalates on its FIRST refusal rather than after three spaced strikes. The sheet is still kept on the
// device: escalation never deletes, and the office records the visit themselves.
export const TERMINAL_REFUSAL_CODES = new Set(["visit_date_too_old"]);

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : "Sync failed";
}

// Which statuses are STRUCTURAL: something wrong with the submission itself, which no number of
// retries will fix.
//
// An explicit ALLOW-LIST, not "every 4xx", because the two failure modes are not symmetric. If we
// wrongly treat a transient failure as structural we strand a signed, completed job sheet on an
// engineer's phone. If we wrongly treat a structural failure as transient it simply retries, which
// is exactly what this app did before and is harmless. So anything not known to be permanent is
// treated as transient.
//
// Deliberately NOT here:
//   401       authentication. The auth context owns that path (Session AEH B1 keeps the session
//             alive rather than bouncing the engineer), and a re-login genuinely fixes it.
//   408 / 425 timeouts and replay protection: transient by definition.
//   429       rate limiting. This app registers a GLOBAL 300-per-minute limiter keyed on IP
//             (server/src/app.ts), so two engineers behind one site hotspot draining their
//             outboxes together can trip it. It clears in under a minute on its own; escalating
//             it would be the worst possible outcome for a problem that fixes itself.
//   404 / 410 never reach here: those are dropped above (the job is gone).
const STRUCTURAL_STATUSES = new Set([400, 403, 409, 422]);

export function isStructuralStatus(status: number): boolean {
  return STRUCTURAL_STATUSES.has(status);
}

// Map an HTTP status to a send outcome. 404/410 mean the job no longer exists to
// complete, so the queued item is dropped rather than retried forever. Auth
// (401/403) and 4xx validation are kept (a re-login or a fix may let it through);
// they surface as a sync error on the job so nothing is silently discarded.
// Session AEK: a 4xx is additionally marked `structural`, and the server's error detail (if any)
// is kept on the item, bounded, so the office has something to diagnose from.
export function classifySendError(status: number, detail?: string): SendResult {
  if (status === 404 || status === 410) return { ok: false, drop: true };
  const trimmed = detail?.trim();
  const base = `Server responded ${status}`;
  const error = trimmed ? `${base}: ${trimmed}`.slice(0, MAX_SYNC_ERROR_CHARS) : base;
  const structural = isStructuralStatus(status);
  return structural
    ? { ok: false, drop: false, error, status, structural: true, ...(trimmed ? { code: trimmed } : {}) }
    : { ok: false, drop: false, error, status };
}

// The plain-English reason shown to the engineer. The engineer never sees a raw HTTP status or a
// raw server message: a code is not something they can act on, and "contact the office" is.
export function escalationReason(status?: number, code?: string): string {
  // Session BBC: the one refusal with its own sentence, because the engineer can act on it.
  if (code === "visit_date_too_old") return VISIT_DATE_TOO_OLD_ESCALATION;
  if (code === "late_reason_required") return "This visit needed a reason for being sent late. Contact the office, who will record it.";
  if (status === 409) return "This record conflicts with an update already received by the office.";
  // Session AGS made 403 genuinely reachable on the completion path: the job must now be assigned to
  // the engineer submitting it, so a visit reassigned while they were underground refuses here. It
  // is the one escalation with a specific, actionable cause, and the generic "the office needs to
  // review this" would send an engineer to describe a problem they could not name. The work is not
  // lost -- 403 is structural, so the sheet is kept and the office is notified -- but only the
  // office can clear it, by putting the visit back.
  if (status === 403) return "This visit is no longer assigned to you. The office needs to assign it back before this can be saved.";
  // 400 and 422 are both "the server would not accept what was sent". 400 is the one the job
  // completion route actually returns (its schema rejects the body), so this is the sentence a
  // real escalation will almost always show; it is worth being the useful one rather than the
  // generic fallback.
  if (status === 422 || status === 400) return "The office needs to check the data before this can be saved.";
  return "The office needs to review this submission.";
}

// True once an item has stopped retrying and is waiting for the office.
export function isEscalated(item: OutboxItem): boolean {
  return item.status === "escalated";
}

// Put an escalated submission back in the retry loop (Session AEK, the recovery path).
//
// Without this the escalated state is a ONE-WAY DOOR: nothing else in the app clears it, so a
// completed and signed job sheet could sit on a phone forever with no way for anyone to deliver
// it, and the office's normal remedy (return the job to the engineer) is blocked too, because the
// queued item keeps the job showing as submitted. That is a worse failure than the retrying-
// forever behaviour this feature replaced, so the engineer gets an explicit way back.
//
// The strike count and the spacing clock are cleared, so a retry that fails again earns a fresh
// three spaced strikes rather than re-escalating on the first attempt. `officeNotifiedAt` is
// cleared too: a second escalation after the office thought they had fixed it is new information
// and worth telling them about, and it cannot spam, because it costs three more spaced failures.
export function clearEscalation(item: OutboxItem): OutboxItem {
  const next = { ...item };
  delete next.status;
  delete next.escalatedAt;
  delete next.escalatedStatus;
  delete next.escalatedCode;
  delete next.structuralFailures;
  delete next.lastStructuralAt;
  delete next.officeNotifiedAt;
  return next;
}

// Called the moment an item escalates, so the caller can log it and tell the office. Kept as an
// injected hook rather than a console call here: this module is deliberately pure.
export interface FlushHooks {
  onEscalate?: (item: OutboxItem) => void | Promise<void>;
  // The clock, injectable so tests are deterministic.
  now?: () => Date;
}

// Drain the outbox once. Processes items in turn (the queue is small and order
// preserves the engineer's sequence), never throwing: a failed item is kept for
// the next attempt, a permanently-gone item is dropped, a success is removed.
//
// An ESCALATED item is skipped entirely (Session AEK / D32): it is not sent, not counted as a
// failure, and not re-attempted -- including by a manual syncNow, which is exactly the case the
// skip exists for. Another identical attempt cannot help; a person at the office can.
// remaining == everything still in the outbox afterwards (failed + escalated).
export async function flushOutbox(store: OutboxStore, send: SendFn, hooks: FlushHooks = {}): Promise<FlushSummary> {
  const now = hooks.now ?? (() => new Date());
  const items = await store.getOutbox();
  let synced = 0;
  let dropped = 0;
  let failed = 0;
  let escalated = 0;
  let alreadyEscalated = 0;

  for (const item of items) {
    if (isEscalated(item)) {
      alreadyEscalated += 1;
      continue;
    }

    let result: SendResult;
    try {
      result = await send(item);
    } catch (err) {
      // A thrown send is a network failure, never a structural one: there was no response.
      result = { ok: false, drop: false, error: errorMessage(err) };
    }

    if (result.ok) {
      await store.deleteOutbox(item.id);
      synced += 1;
      continue;
    }
    if (result.drop) {
      await store.deleteOutbox(item.id);
      dropped += 1;
      continue;
    }

    // Consecutive structural failures only: a transient failure resets the count, so an outage
    // in the middle of a 4xx run does not push an item over the line on a technicality. And a
    // failure only COUNTS once the spacing window has passed (see MIN_STRUCTURAL_GAP_MS), so a
    // burst of drains in a few seconds cannot burn all three strikes.
    const at = now();
    const previous = item.structuralFailures ?? 0;
    const lastCountedAt = item.lastStructuralAt ? Date.parse(item.lastStructuralAt) : NaN;
    const spaced = Number.isNaN(lastCountedAt) || at.getTime() - lastCountedAt >= MIN_STRUCTURAL_GAP_MS;
    const counts = result.structural && spaced;
    const structuralFailures = result.structural ? (counts ? previous + 1 : previous) : 0;

    const next: OutboxItem = {
      ...item,
      attempts: item.attempts + 1,
      lastError: result.error,
      structuralFailures,
      lastStructuralAt: result.structural ? (counts ? at.toISOString() : item.lastStructuralAt) : undefined,
    };

    const terminal = result.structural === true && !!result.code && TERMINAL_REFUSAL_CODES.has(result.code);
    if (result.structural && (terminal || structuralFailures >= ESCALATE_AFTER_STRUCTURAL_FAILURES)) {
      next.status = "escalated";
      next.escalatedAt = at.toISOString();
      next.escalatedStatus = result.status;
      if (result.code) next.escalatedCode = result.code;
      await store.putOutbox(next);
      escalated += 1;
      await hooks.onEscalate?.(next);
      continue;
    }

    await store.putOutbox(next);
    failed += 1;
  }

  return { synced, dropped, failed, escalated, remaining: failed + escalated + alreadyEscalated };
}

// ---- Display merge ---------------------------------------------------------
// A job with a queued completion is shown as complete + pendingSync, so the
// engineer's offline work is reflected immediately. The outbox is the single
// source of truth for "done but not yet confirmed" -- the cached job is not
// mutated.
export function mergeJobsForDisplay(cached: CachedJob[], outbox: OutboxItem[]): JobView[] {
  const pending = new Map(outbox.map((o) => [o.id, o]));
  return cached
    .map((job) => {
      const queued = pending.get(job.id);
      return {
        ...job,
        status: queued ? "complete" : job.status,
        pendingSync: queued != null,
        syncError: queued?.lastError ?? null,
        // The engineer's work is still done and still saved: it simply cannot be sent without the
        // office. So it stays "complete + pending", and gains the reason it is stuck.
        syncEscalated: queued != null && isEscalated(queued),
        syncEscalatedReason: queued != null && isEscalated(queued) ? escalationReason(queued.escalatedStatus, queued.escalatedCode) : null,
      };
    })
    .sort(compareJobViews);
}

// ---- Escalations -----------------------------------------------------------
// One escalated submission, ready to render. Built from the OUTBOX rather than the merged job
// list: the cached job list is replaced wholesale from the server on every refresh, and a job
// whose completion is stuck can legitimately drop out of it, so reading the outbox is what
// guarantees an escalation is never invisible. The job details are a best-effort label.
export interface EscalatedSubmission {
  id: string; // jobId
  jobNumber: string | null;
  eleNumber: string | null;
  siteName: string | null;
  escalatedAt: string | null;
  queuedAt: string;
  reason: string; // plain English, never a status code
}

// The notice the office receives when a submission escalates (Session AEK A4). Deliberately
// self-describing: it lands in the office's message thread with the engineer, so it must say what
// it is, which lift it concerns, why it is stuck, and that the work still exists on the device.
export function escalationOfficeMessage(input: {
  eleNumber?: string | null;
  jobNumber?: string | null;
  siteName?: string | null;
  reason: string;
}): string {
  const label = input.eleNumber ?? input.jobNumber ?? "A completed job sheet";
  const where = input.siteName ? ` at ${input.siteName}` : "";
  return [
    "Automatic notice from the engineer app.",
    `A completed job sheet could not be sent: ${label}${where}.`,
    `Reason: ${input.reason}`,
    "The sheet is saved on the engineer's device and will not send until the office sorts this out.",
  ].join(" ");
}

export function escalatedSubmissions(cached: CachedJob[], outbox: OutboxItem[]): EscalatedSubmission[] {
  const byId = new Map(cached.map((j) => [j.id, j]));
  return outbox
    .filter(isEscalated)
    .map((item) => {
      const job = byId.get(item.id);
      return {
        id: item.id,
        jobNumber: job?.jobNumber ?? null,
        eleNumber: job?.eleNumber ?? null,
        siteName: job?.siteName ?? null,
        escalatedAt: item.escalatedAt ?? null,
        queuedAt: item.queuedAt,
        reason: escalationReason(item.escalatedStatus, item.escalatedCode),
      };
    })
    .sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

// Active jobs first, then done/pending; within each, soonest scheduled first
// (undated last), then a stable tiebreak on id.
export function compareJobViews(a: JobView, b: JobView): number {
  const aDone = a.pendingSync || a.status === "complete";
  const bDone = b.pendingSync || b.status === "complete";
  if (aDone !== bDone) return aDone ? 1 : -1;

  const at = a.scheduledFor ? Date.parse(a.scheduledFor) : Number.POSITIVE_INFINITY;
  const bt = b.scheduledFor ? Date.parse(b.scheduledFor) : Number.POSITIVE_INFINITY;
  if (at !== bt) return at - bt;
  return a.id.localeCompare(b.id);
}
